import { AuthenticatedLayout } from '@shared/components/authenticatedLayout/AuthenticatedLayout'
import { BigSpinner, Notifications } from '@components'
import {
  ClaimEnum,
  localStorageService,
  sessionStorageService,
} from '@services'
import { fetchBrokerTier } from '@store/commonReducers/brokerTier'
import { getAppConfiguration } from './store/commonReducers/config'
import { Navigate, Route, Routes } from 'react-router-dom'
import { OnlyNonAuthSession } from './OnlyNonAuthSession'
import { recoverToken } from '@screens/login/thunk'
import { RequireAuth } from './RequireAuth'
import { RootState, fetchAssessmentEvaluation } from './store'
import { routes } from './navigation/routes'
import { useDispatch, useSelector } from 'react-redux'
import { lazy, Suspense, useEffect, JSX, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useAppFavicon } from 'hooks/useAppFavicon'
import { useAppTitle } from 'hooks/useAppTitle'
import { useDinamycStyles } from 'hooks/useDinamycStyles'
import { getAppFeatures } from '@utils/getAppFeatures'
import { setToken } from '@screens/login/action'
import { IAppManager } from './utils/appManager'
import { isTokenExpired } from '@utils/isTokenExpired'
import { i18nManager } from './i18n'
import { AppLanguages } from 'interfaces'

/**
 * Lazy loading of routes
 */
const ConfirmationPage = lazy(
  () => import('./screens/confirmationPage/confirmationPage'),
)
const LogIn = lazy(() => import('./screens/login/login'))
const SignUp = lazy(() => import('./screens/signup/signup'))
const ResetPassword = lazy(
  () => import('./screens/resetPassword/resetPassword'),
)
const Home = lazy(() => import('./screens/home/home'))
const Discover = lazy(() => import('./screens/discover/discover'))
const Profile = lazy(() => import('./screens/profile/profile'))
const Themes = lazy(() => import('./screens/profile/screens/themes'))
const Languages = lazy(() => import('./screens/profile/screens/languages'))
const PaymentMethods = lazy(
  () => import('./screens/profile/screens/paymentMethods'),
)
const Transactions = lazy(() => import('./screens/transactions/transactions'))
const TransactionDetails = lazy(
  () => import('./screens/transactions/transactionDetails/transactionDetails'),
)
const Collections = lazy(() => import('./screens/collections/collections'))
const CollectionDetails = lazy(
  () => import('./screens/collections/collection/collection'),
)
const PortfolioScreen = lazy(() => import('./screens/portfolio/portfolio'))
const CompanyDetail = lazy(() => import('./screens/company/companyDetail'))
const Help = lazy(() => import('./screens/profile/screens/help'))
const RecurringPaymentDetail = lazy(
  () => import('./screens/recurringPayments/detail/recurringPaymentsDetail'),
)
const RecurringPayments = lazy(
  () => import('./screens/recurringPayments/list/recurringPayments'),
)
const Watchlist = lazy(() => import('./screens/watchlist/watchlist'))

const Chatbot = lazy(() => import('./screens/chatbot/chatbot'))

const Balances = lazy(() => import('./screens/profile/screens/balances'))
// Part of Sentry setup for react router v6
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const features = getAppFeatures()

function App({
  embedded,
  manager,
  defaultLang,
}: {
  embedded: boolean
  manager: IAppManager
  defaultLang: AppLanguages
}): JSX.Element {
  const auth = useSelector((state: RootState) => state.login)
  const dispatch = useDispatch()
  useAppFavicon()
  useAppTitle()
  useDinamycStyles()

  const setNewToken = useCallback((t: string): void => {
    sessionStorageService.setSessionState({
      user: {
        accessToken: t,
        renewalToken: null,
        id: null,
      },
    })
    dispatch(
      setToken({
        token: t,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!embedded) return

    if (!manager?.token) {
      manager?.authenticate((t) => {
        setNewToken(t?.token)
      })
    } else {
      setNewToken(manager?.token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager])

  useEffect(() => {
    if (!auth.token && sessionStorageService.getSessionState()?.user) {
      dispatch(recoverToken())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const supportedLang = (lang: AppLanguages): boolean => {
      return Object.keys(AppLanguages).some((v) => AppLanguages[v] === lang)
    }

    const saved = localStorageService.getLocalState()?.lang as AppLanguages

    const defaultIsValid = supportedLang(defaultLang)
    const savedIsValid = supportedLang(saved)

    let option: AppLanguages = AppLanguages.en
    if (defaultLang && defaultIsValid) {
      option = defaultLang
    } else if (saved && savedIsValid) {
      option = saved
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    i18nManager.changeLanguage(option)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isTester = auth?.claims?.some((c) => c === ClaimEnum.isTester)
    if (auth?.token) {
      dispatch(getAppConfiguration(isTester))
      dispatch(fetchAssessmentEvaluation())
      dispatch(fetchBrokerTier())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.claims, auth.token])

  // Refresh token
  useEffect(() => {
    let timeoutId

    if (auth?.token) {
      const isValid = isTokenExpired(auth?.token)
      if (isValid?.expiresAtIso) {
        const remaining =
          new Date(isValid.expiresAtIso).getTime() - new Date().getTime()
        if (remaining > 0 && manager && manager?.embedded) {
          timeoutId = setTimeout(() => {
            manager?.authenticate((t) => {
              setNewToken(t?.token)
            })
          }, remaining - manager?.refreshTokenSecondsOffset)
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token])

  if (embedded && manager && !auth?.token) return null

  return (
    <>
      {auth.isLoading ? (
        <div className='home__loader --fade-in-animation'>
          <BigSpinner />
        </div>
      ) : (
        <>
          <SentryRoutes>
            {!embedded && (
              <>
                <Route
                  key={routes.nonAuth.login}
                  path={routes.nonAuth.login}
                  element={
                    <OnlyNonAuthSession token={auth?.token}>
                      <Suspense fallback>
                        <LogIn />
                      </Suspense>
                    </OnlyNonAuthSession>
                  }
                />
                <Route
                  key={routes.nonAuth.signup}
                  path={routes.nonAuth.signup}
                  element={
                    <OnlyNonAuthSession token={auth?.token}>
                      <Suspense fallback>
                        <SignUp />
                      </Suspense>
                    </OnlyNonAuthSession>
                  }
                />
                <Route
                  key={routes.auth.resetPassword}
                  path={routes.auth.resetPassword}
                  element={
                    <OnlyNonAuthSession token={auth?.token}>
                      <Suspense fallback>
                        <ResetPassword />
                      </Suspense>
                    </OnlyNonAuthSession>
                  }
                />
              </>
            )}
            <Route
              key={routes.nonAuth.root}
              path={routes.nonAuth.root}
              element={
                <RequireAuth token={auth?.token}>
                  <AuthenticatedLayout />
                </RequireAuth>
              }
            >
              <Route
                key={routes.auth.home}
                path={routes.auth.home}
                element={
                  <Suspense fallback>
                    <Home />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.discover}
                path={routes.auth.discover}
                element={
                  <Suspense fallback>
                    <Discover />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.profile}
                path={routes.auth.profile}
                element={
                  <Suspense fallback>
                    <Profile />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.help}
                path={routes.auth.help}
                element={
                  <Suspense fallback>
                    <Help />
                  </Suspense>
                }
              />
              {!!features?.features?.recurringPayments && (
                <Route
                  key={routes.auth.recurringPayments}
                  path={routes.auth.recurringPayments}
                  element={
                    <Suspense fallback>
                      <RecurringPayments />
                    </Suspense>
                  }
                />
              )}
              <Route
                key={routes.auth.recurringPaymentsDetail}
                path={routes.auth.recurringPaymentsDetail}
                element={
                  <Suspense fallback>
                    <RecurringPaymentDetail />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.themes}
                path={routes.auth.themes}
                element={
                  <Suspense fallback>
                    <Themes />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.language}
                path={routes.auth.language}
                element={
                  <Suspense fallback>
                    <Languages />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.paymentMethods}
                path={routes.auth.paymentMethods}
                element={
                  <Suspense fallback>
                    <PaymentMethods />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.balances}
                path={routes.auth.balances}
                element={
                  <Suspense fallback>
                    <Balances />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.transactions}
                path={routes.auth.transactions}
                element={
                  <Suspense fallback>
                    <Transactions />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.transactionDetail}
                path={routes.auth.transactionDetail}
                element={
                  <Suspense fallback>
                    <TransactionDetails />
                  </Suspense>
                }
              />
              <Route
                path={routes.auth.company}
                element={
                  <Suspense fallback>
                    <CompanyDetail />
                  </Suspense>
                }
              />
              {features?.features?.allowFinny && (
                <Route
                  key={routes.auth.chatbot}
                  path={routes.auth.chatbot}
                  element={<Chatbot />}
                />
              )}
              <Route
                key={routes.auth.collections}
                path={routes.auth.collections}
                element={
                  <Suspense fallback>
                    <Collections />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.collection}
                path={routes.auth.collection}
                element={
                  <Suspense fallback>
                    <CollectionDetails />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.portfolio}
                path={routes.auth.portfolio}
                element={
                  <Suspense fallback>
                    <PortfolioScreen />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.confirmation}
                path={routes.auth.confirmation}
                element={
                  <Suspense fallback>
                    <ConfirmationPage />
                  </Suspense>
                }
              />
              <Route
                key={routes.auth.watchlist}
                path={routes.auth.watchlist}
                element={
                  <Suspense fallback>
                    <Watchlist />
                  </Suspense>
                }
              />
              <Route
                key={'auth-catch-all'}
                path={'/'}
                element={<Navigate to={routes?.auth?.home} />}
              />
            </Route>
            <Route
              key={'catch-all'}
              path={'*'}
              element={
                <Navigate
                  to={embedded ? routes?.auth?.home : routes?.nonAuth?.login}
                />
              }
            />
          </SentryRoutes>
          <Notifications />
        </>
      )}
    </>
  )
}

export default App
